/**
 * An Icon is a single searchable item in the b2b/web-5 media-manager catalog.
 * Each icon carries metadata (name, keywords, collection, category, type, weight,
 * optional description) and a binary stored in Wix Media. The Wix Media `media_url`
 * is the canonical CDN reference.
 *
 * Icons may be global (no `msid` — visible to all clients) or private to a single
 * metasite (`msid` set — visible only to the owning metasite).
 *
 * Storage: metadata + an auto-computed semantic embedding are kept in Vespa via
 * Vfeeder/Vsearch (schema `web5_icon`). The icon binary is in a single shared icons
 * metasite in Wix Media.
 *
 * Note: the underlying Vespa schema name (`web5_icon`) is independent of the entity
 * fqdn — they're decoupled in this service.
 */
export interface Icon {
  /**
   * Icon ID.
   * @format GUID
   * @readonly
   */
  id?: string | null;
  /**
   * Revision number, which increments by 1 each time the Icon is updated.
   * To prevent conflicting changes, the current revision must be passed when updating
   * the Icon.
   *
   * Ignored when creating an Icon.
   * @readonly
   */
  revision?: string | null;
  /**
   * Date and time the Icon was created.
   * @readonly
   */
  createdDate?: Date | null;
  /**
   * Date and time the Icon was last updated.
   * @readonly
   */
  updatedDate?: Date | null;
  /**
   * Human-readable icon name. Searchable (BM25) and contributes to the semantic embedding.
   * @maxLength 200
   */
  name?: string;
  /**
   * Free-form search keywords describing the icon. Searchable (BM25) and contributes
   * to the embedding. Maps to the `tags` field of the underlying Vespa `web5_icon` schema.
   * @maxSize 50
   * @maxLength 100
   */
  keywords?: string[];
  /**
   * Collection the icon belongs to (e.g. "social", "ui", "weather"). Filterable.
   * @maxLength 100
   */
  collection?: string;
  /**
   * Sub-grouping within a collection. Filterable.
   * @maxLength 100
   */
  category?: string;
  /**
   * Free-form type tag (e.g. "outline", "solid"). Filterable.
   * @maxLength 100
   */
  type?: string;
  /**
   * Optional stylistic weight (e.g. "thin", "light", "regular", "bold", "fill", "duotone").
   * Validated against a server-side allow-list, normalized to lowercase, then stored. Filterable.
   * @maxLength 32
   */
  weight?: string | null;
  /**
   * Optional natural-language description of what the icon depicts.
   * Contributes to the semantic embedding and is searchable (BM25).
   * @maxLength 2000
   */
  description?: string | null;
  /**
   * ID of the metasite that owns this private icon. Empty / unset means the icon is
   * global (visible to all clients). Always sourced from the caller's signed context
   * on PUBLIC writes — never honored from the request body.
   * @format GUID
   * @readonly
   */
  msid?: string | null;
  /**
   * ID of the icon binary in Wix Media. Service-assigned during CreateIcon.
   * @maxLength 200
   * @readonly
   */
  mediaFileId?: string;
  /**
   * wixstatic.com CDN URL of the icon binary. Service-assigned during CreateIcon.
   * @format WEB_URL
   * @readonly
   */
  mediaUrl?: string;
  /** Data Extensions */
  extendedFields?: ExtendedFields;
  /**
   * Platform Tags (AIP-7025). Distinct from `keywords` above; see entity-level
   * taggable doc-comment. Managed via the BulkUpdateIconTags / BulkUpdateIconTagsByFilter
   * RPCs in IconService.
   */
  tags?: Tags;
}

export interface ExtendedFields {
  /**
   * Extended field data. Each key corresponds to the namespace of the app that created the extended fields.
   * The value of each key is structured according to the schema defined when the extended fields were configured.
   *
   * You can only access fields for which you have the appropriate permissions.
   *
   * Learn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).
   */
  namespaces?: Record<string, Record<string, any>>;
}

/**
 * Common object for tags.
 * Should be use as in this example:
 * message Foo {
 * option (.wix.api.decomposite_of) = "wix.commons.v2.tags.Foo";
 * string id = 1;
 * ...
 * Tags tags = 5
 * }
 *
 * example of taggable entity
 * {
 * id: "123"
 * tags: {
 * public_tags: {
 * tag_ids:["11","22"]
 * },
 * private_tags: {
 * tag_ids: ["33", "44"]
 * }
 * }
 * }
 */
export interface Tags {
  /** Tags that require an additional permission in order to access them, normally not given to site members or visitors. */
  privateTags?: TagList;
  /** Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors. */
  publicTags?: TagList;
}

export interface TagList {
  /**
   * List of tag IDs.
   * @maxSize 100
   * @maxLength 5
   */
  tagIds?: string[];
}

export interface IconTagsModified {
  /** Updated Icon. */
  icon?: Icon;
  /** Tags that were assigned to the Icon. */
  assignedTags?: Tags;
  /** Tags that were unassigned from the Icon. */
  unassignedTags?: Tags;
}

export interface CreateIconRequest {
  /**
   * Icon metadata to create. Read-only fields on `Icon` (id, revision, created_date,
   * updated_date, msid, media_file_id, media_url) are ignored.
   */
  icon: Icon;
  /**
   * MIME type of the binary (e.g. "image/svg+xml", "image/png").
   * @maxLength 100
   */
  mimeType: string;
  /**
   * Display file name shown in Wix Media (extension included).
   * @maxLength 200
   */
  fileName?: string;
  /** Icon binary. For inline upload via gRPC; capped at 4 MiB (proto default). */
  content: Uint8Array;
  /**
   * INTERNAL ONLY: override the metasite owning this icon. Empty / unset means global.
   * Ignored on PUBLIC calls (PUBLIC always uses the caller's msid from signed context).
   * @internal
   * @format GUID
   */
  privateMsid?: string | null;
}

export interface CreateIconResponse {
  /** The created Icon. */
  icon?: Icon;
}

export interface GetIconRequest {
  /**
   * ID of the Icon to retrieve.
   * @format GUID
   */
  iconId: string;
}

export interface GetIconResponse {
  /** The requested Icon. */
  icon?: Icon;
}

export interface UpdateIconMetadataRequest {
  /** Icon with the fields to update. `id` and `revision` are required. */
  icon: Icon;
  /**
   * Set of fields to update. Inferred from JSON body on REST; explicit on gRPC.
   * @internal
   */
  fieldMask?: string[];
}

export interface UpdateIconMetadataResponse {
  /** Updated Icon. */
  icon?: Icon;
}

export interface DeleteIconRequest {
  /**
   * ID of the Icon to delete.
   * @format GUID
   */
  iconId: string;
}

export interface DeleteIconResponse {}

export interface SearchIconsRequest {
  /**
   * Free-text query. When empty, results are ranked by filter match only (no
   * BM25 / no vector). When non-empty, ranking uses the hybrid rank profile
   * (BM25 blended with vector similarity).
   * @maxLength 1024
   */
  query?: string | null;
  /** Optional filter spec. */
  filter?: IconFilter;
  /** Cursor-based paging. */
  paging?: CursorPaging;
  /**
   * Optional per-query overrides for the hybrid rank weights. Recognized keys:
   * - "text_match_weight": BM25 contribution weight (default 1.0)
   * - "ann_match_weight":  vector-similarity contribution weight (default 1.0)
   */
  rankOverrides?: Record<string, number>;
}

/**
 * Filter spec for SearchIcons. All fields are optional; an absent field means
 * "no constraint on this dimension".
 */
export interface IconFilter {
  /**
   * Match any of these collections (OR semantics).
   * @maxSize 50
   * @maxLength 100
   */
  collections?: string[];
  /**
   * Match any of these categories.
   * @maxSize 50
   * @maxLength 100
   */
  categories?: string[];
  /**
   * Match any of these types.
   * @maxSize 50
   * @maxLength 100
   */
  types?: string[];
  /**
   * Match any of these weights (e.g. "regular", "bold").
   * @maxSize 10
   * @maxLength 32
   */
  weights?: string[];
  /**
   * Match icons whose `keywords` array contains any of these terms.
   * @maxSize 50
   * @maxLength 100
   */
  keywords?: string[];
  /**
   * Match icons whose `name` exactly equals one of these.
   * @maxSize 50
   * @maxLength 200
   */
  names?: string[];
  /**
   * Match icons by Wix Media file ID.
   * @maxSize 50
   * @maxLength 200
   */
  mediaFileIds?: string[];
  /**
   * Substring filter on `description`.
   * @maxLength 200
   */
  descriptionContains?: string | null;
  /** Which tenancy classes to include. */
  tenantScope?: TenantScopeWithLiterals;
  /** Only icons updated on or after this timestamp. */
  updatedAfter?: Date | null;
  /** Only icons updated on or before this timestamp. */
  updatedBefore?: Date | null;
  /**
   * Per-request exclusion list — drops these icon IDs from results regardless of
   * tenancy match. Typical use: hide specific global icons the client doesn't want
   * to see. Capped at 200 IDs per request.
   * @maxSize 200
   * @format GUID
   */
  excludeIds?: string[];
}

/**
 * Scope filter for SearchIcons. Determines whether globals, the caller's privates,
 * or both are returned. Caller-derived msid is always the server-side anchor;
 * this enum only refines which tenancy classes are included.
 */
export enum TenantScope {
  /** Global icons AND the caller's private icons. Default behavior. */
  GLOBAL_AND_OWN = 'GLOBAL_AND_OWN',
  /** Global icons only — exclude all private icons. */
  GLOBAL_ONLY = 'GLOBAL_ONLY',
  /** Caller's private icons only — exclude globals. */
  PRIVATE_ONLY = 'PRIVATE_ONLY',
}

/** @enumType */
export type TenantScopeWithLiterals =
  | TenantScope
  | 'GLOBAL_AND_OWN'
  | 'GLOBAL_ONLY'
  | 'PRIVATE_ONLY';

export interface CursorPaging {
  /**
   * Maximum number of items to return in the results.
   * @max 100
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   * @maxLength 16000
   */
  cursor?: string | null;
}

export interface SearchIconsResponse {
  /** Matched icons, ordered by descending rank score. */
  icons?: SearchedIcon[];
  /** Paging metadata. */
  pagingMetadata?: CursorPagingMetadata;
}

export interface SearchedIcon {
  /** The icon document. */
  icon?: Icon;
  /** Hybrid rank score the document received under the active rank profile. */
  relevance?: number;
}

export interface CursorPagingMetadata {
  /** Number of items returned in current page. */
  count?: number | null;
  /** Cursor strings that point to the next page, previous page, or both. */
  cursors?: Cursors;
  /**
   * Whether there are more pages to retrieve following the current page.
   *
   * + `true`: Another page of results can be retrieved.
   * + `false`: This is the last page.
   */
  hasNext?: boolean | null;
}

export interface Cursors {
  /**
   * Cursor string pointing to the next page in the list of results.
   * @maxLength 16000
   */
  next?: string | null;
  /**
   * Cursor pointing to the previous page in the list of results.
   * @maxLength 16000
   */
  prev?: string | null;
}

export interface BulkUpdateIconTagsRequest {
  /**
   * Icons to update.
   * @minSize 1
   * @maxSize 100
   * @format GUID
   */
  iconIds: string[];
  /** Tags to assign. */
  assignTags?: Tags;
  /** Tags to unassign. */
  unassignTags?: Tags;
}

export interface BulkUpdateIconTagsResponse {
  /**
   * Per-icon result.
   * @minSize 1
   * @maxSize 100
   */
  results?: BulkUpdateIconTagsResult[];
  /** Bulk-operation metadata. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface ItemMetadata {
  /**
   * Item ID. Provided only whenever possible. For example, `itemId` can't be provided when item creation has failed.
   * @format GUID
   */
  id?: string | null;
  /** Index of the item within the request array. Allows for correlation between request and response items. */
  originalIndex?: number;
  /** Whether the requested action for this item was successful. When `false`, the `error` field is returned. */
  success?: boolean;
  /** Details about the error in case of failure. */
  error?: ApplicationError;
}

export interface ApplicationError {
  /** Error code. */
  code?: string;
  /** Description of the error. */
  description?: string;
  /** Data related to the error. */
  data?: Record<string, any> | null;
}

export interface BulkUpdateIconTagsResult {
  /** Metadata for the single-icon update. */
  itemMetadata?: ItemMetadata;
}

export interface BulkActionMetadata {
  /** Number of items that were successfully processed. */
  totalSuccesses?: number;
  /** Number of items that couldn't be processed. */
  totalFailures?: number;
  /** Number of failures without details because detailed failure threshold was exceeded. */
  undetailedFailures?: number;
}

export interface BulkUpdateIconTagsByFilterRequest {
  /** Filter selecting which icons to retag. Empty = all icons. */
  filter: Record<string, any> | null;
  /** Tags to assign. */
  assignTags?: Tags;
  /** Tags to unassign. */
  unassignTags?: Tags;
}

export interface BulkUpdateIconTagsByFilterResponse {
  /**
   * Async job id.
   * @format GUID
   */
  jobId?: string;
}

export interface BulkSeedIconsRequest {
  /**
   * Items to seed. Each item must carry either inline content or a source URL.
   * @minSize 1
   * @maxSize 100
   */
  items: SeedItem[];
}

export interface SeedItem extends SeedItemSourceOneOf {
  /** Inline bytes. */
  content?: Uint8Array;
  /**
   * Externally-hosted URL; service uses Wix Media's files/import.
   * @format WEB_URL
   */
  sourceUrl?: string;
  /**
   * Icon name.
   * @maxLength 200
   */
  name?: string;
  /**
   * Search keywords.
   * @maxSize 50
   * @maxLength 100
   */
  keywords?: string[];
  /**
   * Collection.
   * @maxLength 100
   */
  collection?: string;
  /**
   * Category.
   * @maxLength 100
   */
  category?: string;
  /**
   * Free-form type.
   * @maxLength 100
   */
  type?: string;
  /**
   * Stylistic weight, optional.
   * @maxLength 32
   */
  weight?: string | null;
  /**
   * Natural-language description, optional.
   * @maxLength 2000
   */
  description?: string | null;
  /**
   * MIME type of the binary.
   * @maxLength 100
   */
  mimeType?: string;
  /**
   * Display file name in Wix Media.
   * @maxLength 200
   */
  fileName?: string;
  /**
   * Optional metasite owner. Empty / unset = global.
   * @format GUID
   */
  privateMsid?: string | null;
}

/** @oneof */
export interface SeedItemSourceOneOf {
  /** Inline bytes. */
  content?: Uint8Array;
  /**
   * Externally-hosted URL; service uses Wix Media's files/import.
   * @format WEB_URL
   */
  sourceUrl?: string;
}

export interface BulkSeedIconsResponse {
  /** Per-item result. Aligned by index with the request's `items`. */
  results?: SeedResult[];
}

export interface SeedResult {
  /** Created icon, or unset if creation failed. */
  icon?: Icon;
  /**
   * If creation failed, the error message; empty on success.
   * @maxLength 1024
   */
  error?: string;
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at `16:00` and then again at `16:01`, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entityAsJson?: string;
  /**
   * Indicates the event was triggered by a restore-from-trashbin operation for a previously deleted entity
   * @internal
   */
  triggeredByUndelete?: boolean | null;
  /** Indicates the event was triggered by a restore-from-trashbin operation for a previously deleted entity */
  restoreInfo?: RestoreInfo;
  /**
   * Optional domain-specific metadata defined by the API owner, encoded as JSON.
   * @internal
   */
  additionalMetadataAsJson?: string | null;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntityAsJson?: string;
  /**
   * This field is currently part of the of the EntityUpdatedEvent msg, but scala/node libraries which implements the domain events standard
   * wont populate it / have any reference to it in the API.
   * The main reason for it is that fetching the old entity from the DB will have a performance hit on an update operation so unless truly needed,
   * the developer should send only the new (current) entity.
   * An additional reason is not wanting to send this additional entity over the wire (kafka) since in some cases it can be really big
   * Developers that must reflect the old entity will have to implement their own domain event sender mechanism which will follow the DomainEvent proto message.
   * @internal
   * @deprecated
   */
  previousEntityAsJson?: string | null;
  /**
   * Map of fully qualified field paths to their previous values for fields that changed.
   * For more details please see [AIP](https://dev.wix.com/docs/rnd-general/articles/p13n-guidelines-aips/guidance-aips/design-patterns/7016-events#modified-fields-format)
   * @internal
   */
  modifiedFields?: Record<string, any>;
  /**
   * Optional domain-specific metadata defined by the API owner, encoded as JSON.
   * @internal
   */
  additionalMetadataAsJson?: string | null;
}

export interface EntityDeletedEvent {
  /**
   * Indicates if the entity is sent to trash-bin. only available when trash-bin is enabled
   * @internal
   */
  movedToTrash?: boolean | null;
  /** Entity that was deleted. */
  deletedEntityAsJson?: string | null;
  /**
   * Optional domain-specific metadata defined by the API owner, encoded as JSON.
   * @internal
   */
  additionalMetadataAsJson?: string | null;
}

export interface ActionEvent {
  bodyAsJson?: string;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

/** @docsIgnore */
export type BulkUpdateIconTagsApplicationErrors = {
  code?: 'EMPTY_ASSIGN_AND_UNASSIGN_LISTS';
  description?: string;
  data?: Record<string, any>;
};
/** @docsIgnore */
export type BulkUpdateIconTagsByFilterApplicationErrors = {
  code?: 'EMPTY_ASSIGN_AND_UNASSIGN_LISTS';
  description?: string;
  data?: Record<string, any>;
};
